// Staff sign-in. Same endpoint and session shape as CEL-App and
// evie-dashboard — see src/auth/session.ts.
//
// Only the inspector view is behind this. A customer never sees it.

import { useState, type FormEvent } from 'react';
import { signIn, type Session } from '../auth/session';

type Props = {
  onSignedIn: (session: Session) => void;
  onCancel?: () => void;
};

export function LoginView({ onSignedIn, onCancel }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setBusy(true);
    try {
      onSignedIn(await signIn(email.trim(), password));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <form className="signin__card" onSubmit={submit}>
        <div className="brand">
          Virtual Inspection<span> / staff</span>
        </div>
        <p className="empty-note">
          Sign in to run an inspection call and see the checklist. Joining a call as a customer
          does not need an account.
        </p>

        <label className="signin__field">
          <span className="eyebrow">Email</span>
          <input
            type="email"
            value={email}
            autoComplete="username"
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="signin__field">
          <span className="eyebrow">Password</span>
          <input
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        <div className="signin__actions">
          <button type="submit" className="play" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          {onCancel && (
            <button type="button" className="ghostbtn" onClick={onCancel}>
              Back
            </button>
          )}
        </div>

        {error && (
          <p className="signin__error" role="status">
            {error}
          </p>
        )}
      </form>
    </div>
  );
}
